import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  Building,
  Plus,
  Search,
  Edit,
  Trash2,
  User,
  Loader2,
  Phone,
  Mail,
  MapPin
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const Owners = () => {
  const { token, isManager } = useAuth();
  const [owners, setOwners] = useState([]);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  const [deleteOwner, setDeleteOwner] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    owner_type: "llc",
    client_id: "",
    email: "",
    phone: "",
    address: ""
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [ownersRes, clientsRes] = await Promise.all([
        axios.get(`${API}/owners`, { headers }),
        axios.get(`${API}/clients`, { headers })
      ]);
      setOwners(ownersRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const resetForm = () => {
    setFormData({
      name: "",
      owner_type: "llc",
      client_id: "",
      email: "",
      phone: "",
      address: ""
    });
    setEditingOwner(null);
  };

  const handleOpenDialog = (owner = null) => {
    if (owner) {
      setEditingOwner(owner);
      setFormData({
        name: owner.name,
        owner_type: owner.owner_type,
        client_id: owner.client_id || "",
        email: owner.email || "",
        phone: owner.phone || "",
        address: owner.address || ""
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error("Name is required");
      return;
    }

    // Clean up client_id - convert "none" to null
    const submitData = {
      ...formData,
      client_id: formData.client_id === "none" || !formData.client_id ? null : formData.client_id
    };

    setSaving(true);
    try {
      if (editingOwner) {
        await axios.put(`${API}/owners/${editingOwner.id}`, submitData, { headers });
        toast.success("Owner updated successfully");
      } else {
        await axios.post(`${API}/owners`, submitData, { headers });
        toast.success("Owner created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save owner");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/owners/${deleteOwner.id}`, { headers });
      toast.success("Owner deleted successfully");
      setDeleteOwner(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete owner");
    }
  };

  const filteredOwners = owners.filter(owner =>
    owner.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    owner.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const llcOwners = filteredOwners.filter(o => o.owner_type === "llc");
  const personalOwners = filteredOwners.filter(o => o.owner_type === "personal");

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="page-header">
          <div className="skeleton h-8 w-48" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="skeleton h-40 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const OwnerCard = ({ owner }) => (
    <Card className="hover:shadow-md transition-shadow" data-testid={`owner-${owner.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              owner.owner_type === "llc" ? "bg-blue-100" : "bg-green-100"
            }`}>
              {owner.owner_type === "llc" ? (
                <Building className="w-5 h-5 text-blue-600" />
              ) : (
                <User className="w-5 h-5 text-green-600" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">{owner.name}</h3>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={owner.owner_type === "llc" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}>
                  {owner.owner_type === "llc" ? "LLC" : "Personal"}
                </Badge>
                {owner.client && (
                  <Badge variant="outline" className="text-xs">
                    {owner.client.name}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          {isManager && (
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => handleOpenDialog(owner)} data-testid={`edit-owner-${owner.id}`}>
                <Edit className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setDeleteOwner(owner)} data-testid={`delete-owner-${owner.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
        
        <div className="space-y-2 text-sm">
          {owner.property_count > 0 && (
            <div className="flex items-center gap-2 text-slate-600">
              <Building className="w-4 h-4 text-slate-400" />
              {owner.property_count} {owner.property_count === 1 ? "property" : "properties"}
            </div>
          )}
          {owner.email && (
            <div className="flex items-center gap-2 text-slate-600">
              <Mail className="w-4 h-4 text-slate-400" />
              {owner.email}
            </div>
          )}
          {owner.phone && (
            <div className="flex items-center gap-2 text-slate-600">
              <Phone className="w-4 h-4 text-slate-400" />
              {owner.phone}
            </div>
          )}
          {owner.address && (
            <div className="flex items-center gap-2 text-slate-600">
              <MapPin className="w-4 h-4 text-slate-400" />
              {owner.address}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="animate-fade-in" data-testid="owners-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Owners</h1>
          <p className="text-slate-500 mt-1">
            {owners.length} {owners.length === 1 ? "owner" : "owners"} ({llcOwners.length} LLC, {personalOwners.length} Personal)
          </p>
        </div>
        {isManager && (
          <Button onClick={() => handleOpenDialog()} className="bg-green-700 hover:bg-green-800" data-testid="add-owner-button">
            <Plus className="w-4 h-4 mr-2" />
            Add Owner
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search owners..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="owner-search-input"
          />
        </div>
      </div>

      {filteredOwners.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building className="w-8 h-8 text-slate-400" />
          </div>
          <p className="text-slate-500 mb-4">No owners found</p>
          {isManager && (
            <Button onClick={() => handleOpenDialog()} variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Add your first owner
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {llcOwners.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Building className="w-5 h-5 text-blue-600" />
                LLC Owners ({llcOwners.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {llcOwners.map(owner => (
                  <OwnerCard key={owner.id} owner={owner} />
                ))}
              </div>
            </div>
          )}

          {personalOwners.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-green-600" />
                Personal Owners ({personalOwners.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {personalOwners.map(owner => (
                  <OwnerCard key={owner.id} owner={owner} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingOwner ? "Edit Owner" : "Add New Owner"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Parent Client/Group</Label>
              <Select
                value={formData.client_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, client_id: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="owner-client-select">
                  <SelectValue placeholder="Select client..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No client (standalone)</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name} ({client.client_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Optional - link this LLC/owner to a client group</p>
            </div>

            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., ABC Properties LLC or John Smith"
                data-testid="owner-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Type *</Label>
              <Select
                value={formData.owner_type}
                onValueChange={(value) => setFormData({ ...formData, owner_type: value })}
              >
                <SelectTrigger data-testid="owner-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="llc">LLC</SelectItem>
                  <SelectItem value="personal">Personal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
                data-testid="owner-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(555) 123-4567"
                data-testid="owner-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main St, City, State"
                data-testid="owner-address-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-green-700 hover:bg-green-800" disabled={saving} data-testid="save-owner-button">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingOwner ? "Save Changes" : "Add Owner"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteOwner} onOpenChange={() => setDeleteOwner(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Owner</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteOwner?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Owners;
